import { convex } from "@/lib/convexClient";
import { StorageService } from "@/services/storageService";
import { api } from "../../convex/_generated/api";
import { StoragePaths, type StoragePathSegments } from "./storagePaths";

export interface TrashMetadata {
  originalPath: string;
  movedAt: string;
  reason?: string;
  panorama?: PanoramaRecord;
}

export interface PanoramaRecord {
  id: string;
  name: string;
  private_file_url?: string | null;
  thumbnail_url?: string | null;
  processed_url?: string | null;
  [key: string]: unknown;
}

interface StorageItem {
  name?: string;
  id?: string;
}

interface StorageResultError {
  message: string;
}

const toStorageError = (error: unknown): StorageResultError => ({
  message: error instanceof Error ? error.message : String(error),
});

const stagingBucket = {
  async list(path: string) {
    try {
      const data = await StorageService.listFiles("staging", path);
      return { data, error: null as StorageResultError | null };
    } catch (error) {
      return { data: null, error: toStorageError(error) };
    }
  },
  async download(path: string) {
    try {
      const { data } = await StorageService.downloadFile("staging", path);
      return { data, error: null as StorageResultError | null };
    } catch (error) {
      return { data: null, error: toStorageError(error) };
    }
  },
  async upload(
    path: string,
    file: Blob | File,
    options?: { cacheControl?: string; upsert?: boolean },
  ) {
    try {
      const { data } = await StorageService.uploadPrivate("staging", path, file, options);
      return { data, error: null as StorageResultError | null };
    } catch (error) {
      return { data: null, error: toStorageError(error) };
    }
  },
  async remove(paths: string[]) {
    try {
      await StorageService.removeFiles("staging", paths);
      return { data: null, error: null as StorageResultError | null };
    } catch (error) {
      return { data: null, error: toStorageError(error) };
    }
  },
  async createSignedUrl(path: string, expiresIn: number) {
    try {
      const { data } = await StorageService.getPrivateUrl("staging", path, expiresIn);
      return { data, error: null as StorageResultError | null };
    } catch (error) {
      return { data: null, error: toStorageError(error) };
    }
  },
  async getPublicUrl(path: string) {
    return { data: { publicUrl: await StorageService.getPublicUrl("staging", path) } };
  },
};

const getProjectBasePath = (craftspaceId: string, projectId: string) =>
  `${craftspaceId}/${projectId}`;

const getThumbnailsBasePath = (craftspaceId: string, projectId: string) =>
  `${craftspaceId}/${projectId}/auto-generated`;

const getTrashPanoramaFolder = (craftspaceId: string, projectId: string, panoramaName: string) =>
  StoragePaths.private.trash.panorama(craftspaceId, projectId, panoramaName, "").replace(/\/$/, "");

const resolveTrashSegments = (
  craftspaceId: string,
  projectId: string,
  storageSegments?: StoragePathSegments,
) => ({
  craftspaceKey: storageSegments?.craftspaceKey ?? craftspaceId,
  projectKey: storageSegments?.projectKey ?? projectId,
});

export async function movePanoramaToTrash(
  craftspaceId: string,
  projectId: string,
  panoramaName: string,
  opts?: { reason?: string; panoramaRecord?: PanoramaRecord },
) {
  const sourcePath = getProjectBasePath(craftspaceId, projectId);
  const thumbnailsPath = getThumbnailsBasePath(craftspaceId, projectId);
  const trashPath = getTrashPanoramaFolder(craftspaceId, projectId, panoramaName);
  const prefix = `${panoramaName}__`;

  const moveFilesWithPrefix = async (path: string) => {
    const { data: files, error: listError } = await stagingBucket.list(path);
    if (listError) throw new Error(`Failed to list files: ${listError.message}`);

    let movedCount = 0;
    for (const file of files || []) {
      const fname = (file as StorageItem).name || (file as StorageItem).id;
      if (!fname || String(fname).startsWith(".")) continue;
      if (!String(fname).startsWith(prefix)) continue;
      const filePath = `${path}/${fname}`;
      const trashFilePath = `${trashPath}/${fname}`;

      const { data: blob, error: downloadError } = await stagingBucket.download(filePath);
      if (downloadError) throw new Error(`Failed to download ${filePath}: ${downloadError.message}`);

      const { error: uploadError } = await stagingBucket.upload(trashFilePath, blob, {
        upsert: true,
      });
      if (uploadError) throw new Error(`Failed to upload ${trashFilePath}: ${uploadError.message}`);

      const { error: removeError } = await stagingBucket.remove([filePath]);
      if (removeError) throw new Error(`Failed to remove ${filePath}: ${removeError.message}`);
      movedCount++;
    }
    return movedCount;
  };

  let moved = await moveFilesWithPrefix(sourcePath);
  try {
    moved += await moveFilesWithPrefix(thumbnailsPath);
  } catch {
    // Thumbnails folder may not exist yet.
  }

  // Write metadata to help potential restore later
  const metadata: TrashMetadata = {
    originalPath: sourcePath,
    movedAt: new Date().toISOString(),
    reason: opts?.reason,
    panorama: opts?.panoramaRecord,
  };
  const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: "application/json" });
  await stagingBucket.upload(
    StoragePaths.private.trash.metadata(craftspaceId, projectId, panoramaName),
    metadataBlob,
    { upsert: true },
  );

  // Notify UI that trash content has changed
  notifyTrashContentChanged();

  return { moved };
}

function extractPathFromSignedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const marker = "/storage/v1/object/sign/";
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    const rest = url.substring(idx + marker.length);
    return rest.split("?")[0];
  } catch {
    return null;
  }
}

function inferPanoramaFolderFromAnyUrl(panorama: PanoramaRecord): string | null {
  const candidates = [panorama?.private_file_url, panorama?.thumbnail_url, panorama?.processed_url];
  for (const c of candidates) {
    const path = extractPathFromSignedUrl(c);
    if (!path) continue;
    const parts = path.split("/").filter(Boolean);
    const withoutBucket = parts[0] === "staging" ? parts.slice(1) : parts;
    if (withoutBucket.length < 2) continue;
    const filename = withoutBucket[withoutBucket.length - 1];
    const isThumb = withoutBucket[withoutBucket.length - 2] === "auto-generated";
    const projectKeyIndex = isThumb ? withoutBucket.length - 3 : withoutBucket.length - 2;
    if (projectKeyIndex < 1) continue;
    const craftspaceKey = withoutBucket.slice(0, projectKeyIndex).join("/");
    const projectKey = withoutBucket[projectKeyIndex];
    const panoramaName = filename.includes("__")
      ? filename.split("__")[0]
      : filename.replace(/\.[^/.]+$/, "");
    if (!craftspaceKey || !projectKey || !panoramaName) continue;
    return `${craftspaceKey}/${projectKey}/${panoramaName}`;
  }
  return null;
}

export async function movePanoramaToTrashFromUrls(
  panorama: PanoramaRecord,
  reason: string = "User deleted",
) {
  const folder = inferPanoramaFolderFromAnyUrl(panorama);
  if (!folder) return { moved: 0 };
  const parts = folder.split("/");
  if (parts.length < 3) return { moved: 0 };
  // {craftspaceKey}/{projectKey}/{panoramaName}
  const panoramaName = parts[parts.length - 1];
  const projectKey = parts[parts.length - 2];
  const craftspaceKey = parts.slice(0, -2).join("/");
  return await movePanoramaToTrash(craftspaceKey, projectKey, panoramaName, {
    reason,
    panoramaRecord: panorama,
  });
}

export async function listProjectTrash(
  craftspaceId: string,
  projectId: string,
  storageSegments?: StoragePathSegments,
) {
  const resolved = resolveTrashSegments(craftspaceId, projectId, storageSegments);
  const basePath = StoragePaths.private.trash
    .panorama(resolved.craftspaceKey, resolved.projectKey, "", "")
    .replace(/\/$/, "");
  const { data, error } = await stagingBucket.list(basePath);
  if (error) throw new Error(`Failed to list trash: ${error.message}`);

  const items = [] as Array<{
    name: string;
    path: string;
    fileCount: number;
    metadata?: TrashMetadata | null;
  }>;

  for (const it of data || []) {
    const folder = (it as StorageItem).name || (it as StorageItem).id;
    if (!folder || String(folder).startsWith(".")) continue;
    const folderPath = `${basePath}/${folder}`;
    const { data: files } = await stagingBucket.list(folderPath);

    let metadata: TrashMetadata | null = null;
    try {
      const { data: metaBlob } = await stagingBucket.download(`${folderPath}/_trash_metadata.json`);
      if (metaBlob) metadata = JSON.parse(await metaBlob.text());
    } catch {
      metadata = null;
    }

    items.push({ name: folder, path: folderPath, fileCount: (files || []).length, metadata });
  }

  return items;
}

export async function restorePanoramaFromTrash(
  craftspaceId: string,
  projectId: string,
  panoramaName: string,
  storageSegments?: StoragePathSegments,
) {
  const resolved = resolveTrashSegments(craftspaceId, projectId, storageSegments);
  console.log("🔍 Restoring panorama:", {
    craftspaceId: resolved.craftspaceKey,
    projectId: resolved.projectKey,
    panoramaName,
  });

  const trashPath = getTrashPanoramaFolder(
    resolved.craftspaceKey,
    resolved.projectKey,
    panoramaName,
  );
  const restorePath = getProjectBasePath(resolved.craftspaceKey, resolved.projectKey);
  const restoreThumbnailsPath = getThumbnailsBasePath(
    resolved.craftspaceKey,
    resolved.projectKey,
  );

  console.log("📁 Paths:", { trashPath, restorePath });

  // First, read the metadata to get the original panorama record
  let metadata: TrashMetadata | null = null;
  try {
    const metadataPath = StoragePaths.private.trash.metadata(
      resolved.craftspaceKey,
      resolved.projectKey,
      panoramaName,
    );
    console.log("📄 Reading metadata from:", metadataPath);

    const { data: metaBlob, error: metaError } = await stagingBucket.download(metadataPath);

    if (metaError) {
      console.warn("❌ Failed to read metadata:", metaError);
      // Try to list the trash directory to see what's actually there
      console.log("🔍 Listing trash directory to debug...");
      const { data: trashList, error: listError } = await stagingBucket.list(
        StoragePaths.private.trash.root(resolved.craftspaceKey, resolved.projectKey),
      );

      if (listError) {
        console.error("❌ Failed to list trash root:", listError);
      } else {
        console.log("📋 Trash root contents:", trashList);
      }
    } else if (metaBlob) {
      metadata = JSON.parse(await metaBlob.text());
      console.log("✅ Metadata loaded:", metadata);
    }
  } catch (error) {
    console.warn("❌ Failed to read trash metadata:", error);
  }

  // Move files back to original location
  console.log("📋 Listing files in trash path:", trashPath);
  const { data: files, error } = await stagingBucket.list(trashPath);
  if (error) {
    console.error("❌ Failed to list trash files:", error);
    throw new Error(`Failed to list trash files: ${error.message}`);
  }

  console.log("📁 Files found in trash:", files);

  let restored = 0;
  for (const f of files || []) {
    const fname = (f as StorageItem).name || (f as StorageItem).id;
    if (!fname || String(fname).startsWith("_") || String(fname).startsWith(".")) continue;

    const from = `${trashPath}/${fname}`;
    const isGenerated =
      fname.includes("thumbnail") || fname.includes("compressed_") || fname.includes("processed_");
    const to = `${isGenerated ? restoreThumbnailsPath : restorePath}/${fname}`;

    console.log(`🔄 Moving file: ${from} → ${to}`);

    try {
      const { data: blob, error: downloadError } = await stagingBucket.download(from);
      if (downloadError) {
        console.error(`❌ Failed to download ${from}:`, downloadError);
        throw new Error(`Failed to download ${from}: ${downloadError.message}`);
      }

      const { error: uploadError } = await stagingBucket.upload(to, blob, { upsert: true });
      if (uploadError) {
        console.error(`❌ Failed to upload ${to}:`, uploadError);
        throw new Error(`Failed to restore ${fname}: ${uploadError.message}`);
      }

      const { error: removeError } = await stagingBucket.remove([from]);
      if (removeError) {
        console.error(`❌ Failed to remove ${from}:`, removeError);
        throw new Error(`Failed to remove ${from}: ${removeError.message}`);
      }

      console.log(`✅ Successfully moved: ${fname}`);
      restored++;
    } catch (fileError) {
      console.error(`❌ Error processing file ${fname}:`, fileError);
      throw fileError;
    }
  }

  console.log(`📊 Files restored: ${restored}`);

  // Recreate the panorama record in the database if we have metadata
  if (metadata?.panorama && restored > 0) {
    try {
      console.log("🗄️ Recreating panorama record in database...");
      const panoramaData = metadata.panorama;

      // Generate new signed URLs for the restored files
      let privateFileUrl = "";
      let thumbnailUrl = null;
      let thumbnailWebpUrl = null;

      try {
        // First, check what files actually exist in the restored location
        const { data: restoredFiles, error: listError } = await stagingBucket.list(restorePath);
        const { data: restoredThumbFiles } = await stagingBucket.list(restoreThumbnailsPath);

        if (listError) {
          console.error("❌ Failed to list restored files:", listError);
          throw new Error(`Failed to list restored files: ${listError.message}`);
        }

        console.log("📁 Files found in restored location:", restoredFiles);

        // Find the main panorama file (could be .jpg, .jpeg, .png, .webp)
        const mainFile = restoredFiles?.find((file) => {
          const fileName = (file as StorageItem).name || (file as StorageItem).id;
          return (
            fileName &&
            (fileName === `${panoramaName}.jpg` ||
              fileName === `${panoramaName}.jpeg` ||
              fileName === `${panoramaName}.png` ||
              fileName === `${panoramaName}.webp` ||
              fileName.endsWith(".jpg") ||
              fileName.endsWith(".jpeg") ||
              fileName.endsWith(".png") ||
              fileName.endsWith(".webp"))
          );
        });

        if (!mainFile) {
          console.error("❌ No main panorama file found in restored location");
          throw new Error("No main panorama file found in restored location");
        }

        const mainFileName = (mainFile as StorageItem).name || (mainFile as StorageItem).id;
        console.log("🎯 Main panorama file found:", mainFileName);

        // Get signed URL for main panorama file
        const {
          data: { signedUrl },
        } = await stagingBucket.createSignedUrl(`${restorePath}/${mainFileName}`, 3600); // 1 hour expiry
        privateFileUrl = signedUrl;

        // Find thumbnail files
        const thumbnailFile = restoredThumbFiles?.find((file) => {
          const fileName = (file as StorageItem).name || (file as StorageItem).id;
          return fileName?.includes("thumbnail") && fileName?.endsWith(".jpg");
        });

        if (thumbnailFile) {
          const thumbFileName =
            (thumbnailFile as StorageItem).name || (thumbnailFile as StorageItem).id;
          console.log("🖼️ Thumbnail file found:", thumbFileName);

          const {
            data: { signedUrl: thumbUrl },
          } = await stagingBucket.createSignedUrl(`${restorePath}/${thumbFileName}`, 3600);
          thumbnailUrl = thumbUrl;
        }

        // Find WebP thumbnail
        const webpThumbnailFile = restoredThumbFiles?.find((file) => {
          const fileName = (file as StorageItem).name || (file as StorageItem).id;
          return fileName?.endsWith(".webp") && fileName.includes("thumbnail");
        });

        if (webpThumbnailFile) {
          const webpThumbFileName =
            (webpThumbnailFile as StorageItem).name || (webpThumbnailFile as StorageItem).id;
          console.log("🖼️ WebP thumbnail file found:", webpThumbFileName);

          const {
            data: { signedUrl: thumbWebpUrl },
          } = await stagingBucket.createSignedUrl(`${restorePath}/${webpThumbFileName}`, 3600);
          thumbnailWebpUrl = thumbWebpUrl;
        }
      } catch (urlError) {
        console.warn("⚠️ Failed to generate signed URLs:", urlError);
        // Fallback to public URLs if signed URLs fail
        // We need to find the actual files first
        try {
          const { data: restoredFiles } = await stagingBucket.list(restorePath);
          const { data: restoredThumbFiles } = await stagingBucket.list(restoreThumbnailsPath);

          if (restoredFiles && restoredFiles.length > 0) {
            // Find main file
            const mainFile = restoredFiles.find((file) => {
              const fileName = (file as StorageItem).name || (file as StorageItem).id;
              return (
                fileName &&
                (fileName.endsWith(".jpg") ||
                  fileName.endsWith(".jpeg") ||
                  fileName.endsWith(".png") ||
                  fileName.endsWith(".webp"))
              );
            });

            if (mainFile) {
              const mainFileName = (mainFile as StorageItem).name || (mainFile as StorageItem).id;
              const publicFileUrl = await stagingBucket.getPublicUrl(
                `${restorePath}/${mainFileName}`,
              );
              privateFileUrl = publicFileUrl.data.publicUrl;
            }

            // Find thumbnail
            const thumbnailFile = restoredThumbFiles?.find((file) => {
              const fileName = (file as StorageItem).name || (file as StorageItem).id;
              return fileName?.includes("thumbnail") && fileName?.endsWith(".jpg");
            });

            if (thumbnailFile) {
              const thumbFileName =
                (thumbnailFile as StorageItem).name || (thumbnailFile as StorageItem).id;
              const publicThumbUrl = await stagingBucket.getPublicUrl(
                `${restorePath}/${thumbFileName}`,
              );
              thumbnailUrl = publicThumbUrl.data.publicUrl;
            }

            // Find WebP thumbnail
            const webpThumbnailFile = restoredThumbFiles?.find((file) => {
              const fileName = (file as StorageItem).name || (file as StorageItem).id;
              return fileName?.endsWith(".webp") && fileName.includes("thumbnail");
            });

            if (webpThumbnailFile) {
              const webpThumbFileName =
                (webpThumbnailFile as StorageItem).name || (webpThumbnailFile as StorageItem).id;
              const publicThumbWebpUrl = await stagingBucket.getPublicUrl(
                `${restorePath}/${webpThumbFileName}`,
              );
              thumbnailWebpUrl = publicThumbWebpUrl.data.publicUrl;
            }
          }
        } catch (fallbackError) {
          console.error("❌ Fallback URL generation also failed:", fallbackError);
        }
      }

      console.log("🔗 Generated URLs:", {
        private: privateFileUrl,
        thumbnail: thumbnailUrl,
        thumbnailWebp: thumbnailWebpUrl,
      });

      await convex.mutation(api.panoramas.create, {
        project_id: projectId,
        name: panoramaName,
        file_url: privateFileUrl || undefined,
        private_file_url: privateFileUrl || undefined,
        thumbnail_url: thumbnailUrl || undefined,
        thumbnail_url_webp: thumbnailWebpUrl || undefined,
        width: (panoramaData as Record<string, unknown>)?.width ?? undefined,
        height: (panoramaData as Record<string, unknown>)?.height ?? undefined,
        file_size: (panoramaData as Record<string, unknown>)?.file_size ?? undefined,
        order: (panoramaData as Record<string, unknown>)?.order ?? 0,
        file_status: "public",
        processing_status: "completed",
      });
      console.log("✅ Panorama record recreated successfully");
    } catch (error) {
      console.error("❌ Failed to recreate panorama record:", error);
      throw error;
    }
  } else {
    console.warn("⚠️ No metadata or files restored, skipping database record creation");
  }

  // Clean up empty folder
  try {
    const { data: remaining } = await stagingBucket.list(trashPath);
    if (!remaining || remaining.length === 0) {
      await stagingBucket.remove([trashPath]);
      console.log("🗑️ Cleaned up empty trash folder");
    }
  } catch (error) {
    console.warn("⚠️ Failed to clean up empty trash folder:", error);
  }

  console.log("🎉 Panorama restoration completed successfully");

  // Notify UI that trash content has changed
  notifyTrashContentChanged();

  return { restored };
}

export async function emptyProjectTrash(
  craftspaceId: string,
  projectId: string,
  storageSegments?: StoragePathSegments,
) {
  const resolved = resolveTrashSegments(craftspaceId, projectId, storageSegments);
  const basePath = StoragePaths.private.trash.root(resolved.craftspaceKey, resolved.projectKey);
  // Collect all nested paths to delete
  async function collect(path: string, acc: string[]) {
    const { data } = await stagingBucket.list(path);
    for (const it of data || []) {
      const name = (it as StorageItem).name || (it as StorageItem).id;
      if (!name || String(name).startsWith(".")) continue;
      const full = `${path}/${name}`;
      acc.push(full);
      await collect(full, acc);
    }
  }

  const paths: string[] = [];
  await collect(basePath, paths);
  if (paths.length === 0) return { deleted: 0 };

  const { error: removeError } = await stagingBucket.remove(paths);
  if (removeError) throw new Error(`Failed to empty trash: ${removeError.message}`);

  // Notify UI that trash content has changed
  notifyTrashContentChanged();

  return { deleted: paths.length };
}

// Backward compatibility: check for old craftspace-level trash items
export async function listLegacyCraftspaceTrash(craftspaceId: string, projectId: string) {
  const legacyPath = StoragePaths.private.trash.root(craftspaceId, projectId);
  try {
    const { data, error } = await stagingBucket.list(legacyPath);
    if (error || !data || data.length === 0) return [];

    const items = [] as Array<{
      name: string;
      path: string;
      fileCount: number;
      metadata?: TrashMetadata | null;
      isLegacy: boolean;
    }>;

    for (const it of data || []) {
      const folder = (it as StorageItem).name || (it as StorageItem).id;
      if (!folder || String(folder).startsWith(".")) continue;
      const folderPath = `${legacyPath}/${folder}`;
      const { data: files } = await stagingBucket.list(folderPath);

      let metadata: TrashMetadata | null = null;
      try {
        const { data: metaBlob } = await stagingBucket.download(
          `${folderPath}/_trash_metadata.json`,
        );
        if (metaBlob) metadata = JSON.parse(await metaBlob.text());
      } catch {
        metadata = null;
      }

      items.push({
        name: folder,
        path: folderPath,
        fileCount: (files || []).length,
        metadata,
        isLegacy: true,
      });
    }

    return items;
  } catch {
    return [];
  }
}

// Migrate legacy trash items to new project-local structure
export async function migrateLegacyTrash(
  craftspaceId: string,
  projectId: string,
  storageSegments?: StoragePathSegments,
) {
  const legacyItems = await listLegacyCraftspaceTrash(craftspaceId, projectId);
  if (legacyItems.length === 0) return { migrated: 0 };
  const resolved = resolveTrashSegments(craftspaceId, projectId, storageSegments);

  let migrated = 0;
  for (const item of legacyItems) {
    try {
      // Move files from legacy path to new path
      const { data: files } = await stagingBucket.list(item.path);

      for (const f of files || []) {
        const fname = (f as StorageItem).name || (f as StorageItem).id;
        if (!fname || String(fname).startsWith("_") || String(fname).startsWith(".")) continue;

        const from = `${item.path}/${fname}`;
        const to = StoragePaths.private.trash
          .panorama(resolved.craftspaceKey, resolved.projectKey, item.name, fname)
          .replace(/\/$/, "");

        // Download and upload to new location
        const { data: blob } = await stagingBucket.download(from);
        if (blob) {
          await stagingBucket.upload(to, blob, { upsert: true });
          await stagingBucket.remove([from]);
        }
      }

      // Move metadata file
      try {
        const { data: metaBlob } = await stagingBucket.download(
          `${item.path}/_trash_metadata.json`,
        );
        if (metaBlob) {
          await stagingBucket.upload(
            StoragePaths.private.trash.metadata(
              resolved.craftspaceKey,
              resolved.projectKey,
              item.name,
            ),
            metaBlob,
            { upsert: true },
          );
          await stagingBucket.remove([`${item.path}/_trash_metadata.json`]);
        }
      } catch (error) {
        console.warn("Failed to move metadata file:", error);
      }

      // Remove empty legacy folder
      try {
        await stagingBucket.remove([item.path]);
      } catch (error) {
        console.warn("Failed to remove legacy folder:", error);
      }

      migrated++;
    } catch (error) {
      console.warn("Failed to migrate legacy trash item:", item.name, error);
    }
  }

  return { migrated };
}

// Enhanced list function that includes both current and legacy items
export async function listProjectTrashWithLegacy(
  craftspaceId: string,
  projectId: string,
  storageSegments?: StoragePathSegments,
) {
  const currentItems = await listProjectTrash(craftspaceId, projectId, storageSegments);
  const legacyItems = await listLegacyCraftspaceTrash(craftspaceId, projectId);

  // Add isLegacy flag to current items
  const currentWithFlag = currentItems.map((item) => ({ ...item, isLegacy: false }));

  return [...currentWithFlag, ...legacyItems];
}

// Helper function to notify UI of trash content changes
function notifyTrashContentChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("trash-content-changed"));
  }
}
